/**
 * SchedulingSettings — manage booking links + weekly availability.
 *
 * Two cards:
 *   1. Availability — per day of week, set start/end times
 *   2. Booking links — create/edit/delete + copy public URL
 *
 * Studios share a public link like `/book/discovery-call` with leads,
 * who pick a slot from the availability grid and submit their info.
 */

import { useEffect, useState } from 'react';
import { Plus, Calendar, CalendarClock, Link as LinkIcon, Copy, Trash2, Edit2, Video, Phone, MapPin, Clock, ExternalLink } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  useBookingLinks,
  useCreateBookingLink,
  useUpdateBookingLink,
  useDeleteBookingLink,
  useAvailabilityRules,
  useUpsertAvailabilityRule,
  useDeleteAvailabilityRule,
  useStudioTimezone,
  slugify,
  DAY_NAMES,
  LOCATION_TYPE_LABELS,
  type BookingLink,
  type LocationType,
} from '@/services/scheduler';
import { useOrganization, useUpdateOrganization } from '@/services/organizations';
import ExternalCalendarsSection from '@/components/settings/ExternalCalendarsSection';
import { NovaSpinner } from '@/components/nova';
import { useShareOrigin } from '@/hooks/useShareOrigin';

// Curated list of common IANA timezones — covers ~95% of beta studios.
// The picker also offers the browser-detected zone at the top so the
// usual case ("just use my timezone") is a one-tap fix.
const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: 'America/Los_Angeles', label: 'Pacific — Los Angeles' },
  { value: 'America/Denver',      label: 'Mountain — Denver' },
  { value: 'America/Chicago',     label: 'Central — Chicago' },
  { value: 'America/New_York',    label: 'Eastern — New York' },
  { value: 'America/Toronto',     label: 'Eastern — Toronto' },
  { value: 'America/Vancouver',   label: 'Pacific — Vancouver' },
  { value: 'America/Phoenix',     label: 'Arizona (no DST)' },
  { value: 'America/Anchorage',   label: 'Alaska' },
  { value: 'Pacific/Honolulu',    label: 'Hawaii' },
  { value: 'Europe/London',       label: 'UK — London' },
  { value: 'Europe/Paris',        label: 'CET — Paris' },
  { value: 'Europe/Berlin',       label: 'CET — Berlin' },
  { value: 'Europe/Madrid',       label: 'CET — Madrid' },
  { value: 'Europe/Amsterdam',    label: 'CET — Amsterdam' },
  { value: 'Australia/Sydney',    label: 'AEST — Sydney' },
  { value: 'Australia/Melbourne', label: 'AEST — Melbourne' },
  { value: 'Asia/Tokyo',          label: 'JST — Tokyo' },
  { value: 'Asia/Singapore',      label: 'SGT — Singapore' },
  { value: 'Asia/Dubai',          label: 'GST — Dubai' },
  { value: 'UTC',                 label: 'UTC (no offset)' },
];

/** Returns the browser's detected IANA timezone string. Falls back
 *  to UTC if the environment doesn't expose `Intl`. */
function detectBrowserTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && typeof tz === 'string' ? tz : 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Human-friendly label for a timezone string. Looks up the curated
 *  list first, falls back to the raw IANA name. */
function labelForTimezone(tz: string): string {
  return TIMEZONE_OPTIONS.find((o) => o.value === tz)?.label ?? tz;
}

const SchedulingSettings = () => {
  return (
    <div className="space-y-6">
      <div>
        {/* #494 Pass 5 — Editorial section header. Marcom name is
            "The Scheduler" — see docs/marcom-glossary.md. */}
        <p className="orbit-eyebrow">Booking</p>
        <h2 className="font-editorial text-3xl md:text-4xl text-foreground leading-none mt-2">
          The Scheduler
        </h2>
        <p className="text-muted-foreground mt-3">
          Public booking links for discovery calls, consultations, and any other appointments. Leads pick a slot, you get the booking + an inquiry in one go.
        </p>
      </div>
      <TimezoneCard />
      <AvailabilityCard />
      <ExternalCalendarsSection />
      <BookingLinksCard />
    </div>
  );
};

// ─── Timezone ────────────────────────────────────────────────────
//
// Sits ABOVE the availability card because every time the studio
// types into "Monday 9am - 5pm" they need to know which timezone
// those numbers are being interpreted in. On first visit, if the
// org is still on the 'UTC' default we silently update it to the
// browser-detected zone so existing studios don't have to manually
// fix the bug that landed in their settings.
function TimezoneCard() {
  const { data: org } = useOrganization();
  const updateOrg = useUpdateOrganization();
  const { data: studioTz = 'UTC', refetch } = useStudioTimezone(org?.id);
  const browserTz = detectBrowserTimezone();

  // Auto-detect & save once per org if still on the default UTC.
  // This is the bandaid for studios who never saw a timezone UI
  // and are silently broken on their public booking page.
  const [autoUpdated, setAutoUpdated] = useState(false);
  useEffect(() => {
    if (autoUpdated) return;
    if (!org?.id) return;
    if (studioTz !== 'UTC') return;
    if (browserTz === 'UTC') return; // would be a no-op
    setAutoUpdated(true);
    updateOrg.mutate(
      { id: org.id, timezone: browserTz } as any,
      {
        onSuccess: () => {
          refetch();
          toast.success(`Timezone set to ${labelForTimezone(browserTz)}`, {
            description: 'We detected this from your browser. Change it below if it\'s wrong.',
          });
        },
      }
    );
  }, [org?.id, studioTz, browserTz, autoUpdated, updateOrg, refetch]);

  const handleChange = (tz: string) => {
    if (!org?.id) return;
    updateOrg.mutate(
      { id: org.id, timezone: tz } as any,
      {
        onSuccess: () => {
          refetch();
          toast.success(`Timezone updated to ${labelForTimezone(tz)}`);
        },
        onError: (e: any) => toast.error(e?.message ?? 'Could not update timezone'),
      }
    );
  };

  // Show the browser-detected zone at the top if it's NOT already in
  // the curated list, so studios in less common zones still have a
  // one-tap "use my system" option.
  const showBrowserOption = browserTz !== 'UTC' && !TIMEZONE_OPTIONS.some((o) => o.value === browserTz);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <OrbitIcon name="clock" fallback={Clock} className="w-5 h-5" />
          Timezone
        </CardTitle>
        <CardDescription>
          The timezone your availability hours are interpreted in. Leads on the public booking page see slots converted to their own timezone automatically.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <div className="flex-1">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Business timezone</Label>
            <Select value={studioTz} onValueChange={handleChange}>
              <SelectTrigger className="mt-1.5 w-full sm:w-[320px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {showBrowserOption && (
                  <SelectItem value={browserTz}>{browserTz} (detected)</SelectItem>
                )}
                {TIMEZONE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {browserTz !== studioTz && browserTz !== 'UTC' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleChange(browserTz)}
              className="sm:self-end"
            >
              Use my system ({labelForTimezone(browserTz)})
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Availability ────────────────────────────────────────────────

function AvailabilityCard() {
  const { data: rules = [], isLoading } = useAvailabilityRules();
  const upsertRule = useUpsertAvailabilityRule();
  const deleteRule = useDeleteAvailabilityRule();
  // Surface the active studio timezone in the description so the
  // studio sees the context their typed times are interpreted in.
  // Was previously a vague "your local timezone" — which was a lie:
  // if the org's timezone column hadn't been set, the public booking
  // page interpreted these hours as UTC and slots showed 6–8h offset.
  const { data: org } = useOrganization();
  const { data: studioTz = 'UTC' } = useStudioTimezone(org?.id);

  const rulesByDay = DAY_NAMES.map((_, dow) => ({
    dow,
    rules: rules.filter((r) => r.day_of_week === dow),
  }));

  const addDefaultDay = (dow: number) => {
    upsertRule.mutate({ day_of_week: dow, start_time: '09:00', end_time: '17:00' });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <OrbitIcon name="calendar" fallback={Calendar} className="w-5 h-5" />
          Weekly availability
        </CardTitle>
        <CardDescription>
          When you're available for bookings. Times are in <strong>{labelForTimezone(studioTz)}</strong> — change above. Leave a day empty to be unbookable on that day.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-6">
            <NovaSpinner size={20} />
          </div>
        ) : (
          <div className="space-y-2">
            {rulesByDay.map(({ dow, rules: dayRules }) => (
              <div key={dow} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                <div className="w-28 font-medium text-sm">{DAY_NAMES[dow]}</div>
                <div className="flex-1 space-y-2">
                  {dayRules.length === 0 ? (
                    <div className="text-sm text-muted-foreground italic">Unavailable</div>
                  ) : (
                    dayRules.map((rule) => (
                      <div key={rule.id} className="flex items-center gap-2">
                        <Input
                          type="time"
                          defaultValue={rule.start_time}
                          onBlur={(e) => {
                            if (e.target.value !== rule.start_time) {
                              upsertRule.mutate({
                                id: rule.id,
                                day_of_week: dow,
                                start_time: e.target.value,
                                end_time: rule.end_time,
                              });
                            }
                          }}
                          className="w-32 h-8"
                        />
                        <span className="text-muted-foreground">→</span>
                        <Input
                          type="time"
                          defaultValue={rule.end_time}
                          onBlur={(e) => {
                            if (e.target.value !== rule.end_time) {
                              upsertRule.mutate({
                                id: rule.id,
                                day_of_week: dow,
                                start_time: rule.start_time,
                                end_time: e.target.value,
                              });
                            }
                          }}
                          className="w-32 h-8"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteRule.mutate(rule.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <OrbitIcon name="trash" fallback={Trash2} className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => addDefaultDay(dow)}>
                  <OrbitIcon name="plus" fallback={Plus} className="w-3.5 h-3.5 mr-1" />
                  {dayRules.length === 0 ? 'Add hours' : 'Add'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Booking Links ───────────────────────────────────────────────

function BookingLinksCard() {
  const { data: links = [], isLoading } = useBookingLinks();
  const [editing, setEditing] = useState<BookingLink | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <OrbitIcon name="link" fallback={LinkIcon} className="w-5 h-5" />
              Booking links
            </CardTitle>
            <CardDescription>One per appointment type. Share the public URL with leads.</CardDescription>
          </div>
          <Button onClick={() => setCreating(true)}>
            <OrbitIcon name="plus" fallback={Plus} className="w-4 h-4 mr-2" />
            New link
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-6">
            <NovaSpinner size={20} />
          </div>
        ) : links.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <OrbitIcon name="calendar.badge.clock" fallback={CalendarClock} className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No booking links yet. Create one to start taking discovery calls.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {links.map((link) => (
              <BookingLinkRow key={link.id} link={link} onEdit={() => setEditing(link)} />
            ))}
          </div>
        )}
      </CardContent>

      {(creating || editing) && (
        <BookingLinkEditor
          link={editing}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
        />
      )}
    </Card>
  );
}

function BookingLinkRow({ link, onEdit }: { link: BookingLink; onEdit: () => void }) {
  const updateLink = useUpdateBookingLink();
  const deleteLink = useDeleteBookingLink();
  const shareOrigin = useShareOrigin();
  const url = `${shareOrigin}/book/${link.slug}`;

  const Icon = link.location_type === 'video' ? Video :
               link.location_type === 'phone' ? Phone :
               MapPin;

  return (
    <div className="flex items-center gap-3 p-3 rounded-md border border-border">
      <div className={cn(
        'shrink-0 w-9 h-9 rounded-md flex items-center justify-center',
        link.is_active ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
      )}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm flex items-center gap-2">
          {link.name}
          {!link.is_active && (
            <span className="text-xs text-muted-foreground italic">Disabled</span>
          )}
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
          <span>{link.duration_minutes} min · {LOCATION_TYPE_LABELS[link.location_type]}</span>
          <span className="opacity-50">·</span>
          {/* Public URL is a proper anchor now (#355) — tapping opens
              the booking page in a new tab. Click-to-copy stayed as a
              dedicated icon button so both patterns are discoverable. */}
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono truncate underline decoration-dotted underline-offset-2 hover:text-foreground"
            title="Preview booking page in a new tab"
          >
            {url}
          </a>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Switch
          checked={link.is_active}
          onCheckedChange={(checked) => updateLink.mutate({ id: link.id, is_active: checked })}
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            navigator.clipboard.writeText(url);
            toast.success('Link copied');
          }}
          title="Copy public URL"
        >
          <OrbitIcon name="doc.on.doc" fallback={Copy} className="w-3.5 h-3.5" />
        </Button>
        {/* Preview — opens the public /book/:slug page in a new tab
            so studios can sanity-check what leads will see without
            digging up the URL. Requested in #355. */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
          title="Preview booking page in a new tab"
        >
          <OrbitIcon name="arrow.up.right.square" fallback={ExternalLink} className="w-3.5 h-3.5" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onEdit} title="Edit booking link">
          <OrbitIcon name="square.and.pencil" fallback={Edit2} className="w-3.5 h-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (confirm(`Delete "${link.name}"? Existing bookings remain.`)) {
              deleteLink.mutate(link.id);
            }
          }}
          className="text-destructive hover:text-destructive"
          title="Delete booking link"
        >
          <OrbitIcon name="trash" fallback={Trash2} className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

function BookingLinkEditor({
  link,
  onClose,
}: {
  link: BookingLink | null;
  onClose: () => void;
}) {
  const isEditing = !!link;
  const createLink = useCreateBookingLink();
  const updateLink = useUpdateBookingLink();

  const [name, setName] = useState(link?.name ?? '');
  const [slug, setSlug] = useState(link?.slug ?? '');
  const [description, setDescription] = useState(link?.description ?? '');
  const [duration, setDuration] = useState(link?.duration_minutes ?? 30);
  const [bufferBefore, setBufferBefore] = useState(link?.buffer_before_minutes ?? 0);
  const [bufferAfter, setBufferAfter] = useState(link?.buffer_after_minutes ?? 0);
  const [locationType, setLocationType] = useState<LocationType>(link?.location_type ?? 'video');
  const [locationDetails, setLocationDetails] = useState(link?.location_details ?? '');

  const handleNameChange = (n: string) => {
    setName(n);
    if (!isEditing && (slug === '' || slug === slugify(name))) {
      setSlug(slugify(n));
    }
  };

  const handleSave = async () => {
    if (!name.trim() || !slug.trim()) {
      toast.error('Name and URL slug are required');
      return;
    }
    try {
      if (isEditing && link) {
        await updateLink.mutateAsync({
          id: link.id,
          name,
          slug,
          description: description || null,
          duration_minutes: duration,
          buffer_before_minutes: bufferBefore,
          buffer_after_minutes: bufferAfter,
          location_type: locationType,
          location_details: locationDetails || null,
        });
        toast.success('Saved');
      } else {
        await createLink.mutateAsync({
          name,
          slug,
          description: description || null,
          duration_minutes: duration,
          buffer_before_minutes: bufferBefore,
          buffer_after_minutes: bufferAfter,
          location_type: locationType,
          location_details: locationDetails || null,
        });
        toast.success('Booking link created');
      }
      onClose();
    } catch (e: any) {
      toast.error('Failed: ' + (e?.message ?? 'unknown'));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit booking link' : 'New booking link'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="link-name">Name</Label>
            <Input
              id="link-name"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g. Discovery call"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="link-slug">URL slug</Label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground">
                /book/
              </span>
              <Input
                id="link-slug"
                value={slug}
                onChange={(e) => setSlug(slugify(e.target.value))}
                className="rounded-l-none font-mono text-sm"
                placeholder="discovery-call"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="link-desc">Description (shown on the booking page)</Label>
            <Textarea
              id="link-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Quick 30-min call to talk through your project..."
              rows={3}
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Duration</Label>
              <Select value={String(duration)} onValueChange={(v) => setDuration(parseInt(v, 10))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 min</SelectItem>
                  <SelectItem value="30">30 min</SelectItem>
                  <SelectItem value="45">45 min</SelectItem>
                  <SelectItem value="60">60 min</SelectItem>
                  <SelectItem value="90">90 min</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Buffer before</Label>
              <Select value={String(bufferBefore)} onValueChange={(v) => setBufferBefore(parseInt(v, 10))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">None</SelectItem>
                  <SelectItem value="15">15 min</SelectItem>
                  <SelectItem value="30">30 min</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Buffer after</Label>
              <Select value={String(bufferAfter)} onValueChange={(v) => setBufferAfter(parseInt(v, 10))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">None</SelectItem>
                  <SelectItem value="15">15 min</SelectItem>
                  <SelectItem value="30">30 min</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Location</Label>
            <Select value={locationType} onValueChange={(v) => setLocationType(v as LocationType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="video">Video call</SelectItem>
                <SelectItem value="phone">Phone call</SelectItem>
                <SelectItem value="in_person">In person</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="link-loc-details">
              {locationType === 'video' ? 'Video link or platform' :
               locationType === 'phone' ? 'Phone number' :
               locationType === 'in_person' ? 'Address' : 'Details'}
            </Label>
            <Input
              id="link-loc-details"
              value={locationDetails}
              onChange={(e) => setLocationDetails(e.target.value)}
              placeholder={
                locationType === 'video' ? 'Zoom link sent on confirmation' :
                locationType === 'phone' ? 'I\'ll call you' :
                'Business address or meeting place'
              }
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={createLink.isPending || updateLink.isPending}>
            {(createLink.isPending || updateLink.isPending) ? (
              <NovaSpinner size={16} className="mr-2" />
            ) : null}
            {isEditing ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SchedulingSettings;
